import type { BlogPost } from '../types';

export const aboutUsPost: BlogPost = {
  slug: 'about-us',
  title: 'About Us: The Team Behind Sqwish',
  excerpt: 'We are a research and infrastructure company teaching AI systems to learn from what happens after they ship. Here is who we are, what we care about, and where we came from.',
  date: '2026-05-07',
  authors: ['Sqwish Team'],
  tags: ['team', 'culture', 'about', 'values'],
  published: true,
  content: [
    {
      type: 'paragraph',
      content: 'Sqwish is a research and infrastructure company building the outcome optimization layer for generative AI. We close the loop between what an AI system does and what actually happens next, so that real production signals, not benchmarks, decide how the system behaves.',
    },
    {
      type: 'paragraph',
      content: 'Most AI products stop learning the moment they ship. We think that is the wrong place to stop. The question that drives us is simple to ask and hard to answer: did this decision help the person on the other end, and how do we make the next one better?',
    },
    {
      type: 'heading',
      content: 'We Do the Hard Thing on Purpose',
      level: 2,
    },
    {
      type: 'paragraph',
      content: 'We are one company with one mission that pushes the frontier on two sides at once. The research problem is open: how a system adapts, in real time, to outcomes it has never seen; reinforcement learning in the wild rather than a clean simulator, learning from sparse, noisy, delayed signals, and packing all of it into models small enough to decide in the hot path. The engineering problem is just as unforgiving: hosting, routing, and reshaping thousands of requests per second, with live ML models in the path of every one, under tight latency, cost, and safety constraints, without ever dropping reliability. Neither is something you can buy off a shelf. We build both in lockstep, because the loop only closes if they move together.',
    },
    {
      type: 'paragraph',
      content: 'That is what makes the work hard in the best way. Research rewards running many strange experiments in parallel and letting reality kill the weak ones fast. Real-time infrastructure rewards the opposite discipline: microseconds that matter, clean rollbacks, observability everywhere, and the judgment to know when something is not ready to touch production. We refuse to let "move fast" become an excuse for fragile systems, or "quality" become an excuse to stop exploring. Living in that tension is the whole point, and it is what we get out of bed for.',
    },
    {
      type: 'heading',
      content: 'What We Care About',
      level: 2,
    },
    {
      type: 'list',
      content: 'More than any process, a few shared beliefs hold us together:',
      items: [
        'Reality beats narrative. We trust what users do, what systems measure, and what breaks in production over whatever sounds good in a meeting.',
        'High standards, low ego. The best idea should win fast, even when it started as someone else\'s, and especially when it kills one of our own.',
        'Learn out loud. Code pairing, paper clubs, blunt-but-kind feedback: the company gets smarter exactly as fast as the people in it do.',
        'Own the outcome. Nobody hides behind a job description. If something matters and no one has it, we pick it up.',
        'Stay light. We take the work seriously and ourselves a lot less so, and that is what carries us through the genuinely hard days.',
      ],
    },
    {
      type: 'heading',
      content: 'How It Feels',
      level: 2,
    },
    {
      type: 'paragraph',
      content: 'It feels like being in a room with people who are a little obsessed, in the best way. The culture is part Cambridge and part startup: curious and precise, allergic to hand-waving, and at the same time fast, direct, and most alive when a prototype teaches us something the plan got wrong. We are increasingly together around our Cambridge base, with enough flexibility to keep working with brilliant people wherever they happen to be.',
    },
    {
      type: 'paragraph',
      content: 'And we have our quirks. A well-aimed cat meme dropped into the middle of a serious debate. A running, never-resolved argument about what actually counts as "shipped". None of it is the point, and all of it is why the place hums. You can care enormously about a hard problem and still genuinely like the people you are solving it with, and we think that is the only kind of team worth building.',
    },
    {
      type: 'heading',
      content: 'Where It Came From',
      level: 2,
    },
    {
      type: 'paragraph',
      content: 'The two founders, Federica and Ushnish, met at Cambridge in 2018, both already a little obsessed with AI from opposite ends of it: one doing computer vision for robotics and autonomous driving, the other a PhD using machine learning under uncertainty to forecast instabilities in rockets and jet engines. They were friends for years before they were ever co-founders, then colleagues at the world\'s largest smartphone chipmaker in 2022 building tiny AI models that ended up running on billions of devices. Between them they carry the exact mix this company is built on: research that holds up at a top conference, and engineering that holds up in production at scale.',
    },
    {
      type: 'paragraph',
      content: 'Sqwish was born out of getting it wrong first. Their earlier startup was an LLM tool for creative thinking, and building it dragged the real problem into the light: more than half their time vanished into tuning prompts, wrangling context, and second-guessing model choices, with no honest way to know whether any of it made things better for the people actually using the product. The system never learned from what happened next, so the engineers had to be the learning system instead, by hand, forever. That quiet frustration is where Sqwish started. Having lived inside that grind, we stopped caring about what looks elegant in a test and started caring, almost stubbornly, about what is real.',
    },
    {
      type: 'paragraph',
      content: 'If that sounds like your kind of problem, and your kind of room, you will probably recognise the feeling already: careful thinking, quick movement, generous teammates, and a slightly unreasonable belief that AI can be made to work far better in the real world. We would love to build it with you.',
    },
  ],
};
